import * as React from 'react';
import { FaSort, FaSortUp, FaSortDown, FaTrash } from 'react-icons/fa';
import { Employee } from '../../../hooks/useEmployeeData';
import { Absence } from '../services/AbsenceService';
import { SortColumn, getEmpName, formatStartEnd, computeDayCount, formatReqOnShort } from './absencesUtils';
import styles from './Absences.module.css';

interface AbsenceTableProps {
  list: Absence[];
  allEmployees: Employee[];
  selectedIds: Set<number>;
  sortBy: SortColumn;
  sortDir: 'asc' | 'desc';
  onSort: (col: SortColumn) => void;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: (list: Absence[]) => void;
  onRowClick: (abs: Absence, colName: string) => void;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onDelete: (id: number) => void;
}

export function AbsenceTable({
  list,
  allEmployees,
  selectedIds,
  sortBy,
  sortDir,
  onSort,
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  onApprove,
  onReject,
  onDelete,
}: AbsenceTableProps): React.JSX.Element {
  const tableId = 'absences';

  const renderSortIcon = (col: SortColumn): React.ReactNode => {
    const isActive = sortBy === col;
    const color = isActive ? '#0E1333' : '#94a3b8';
    const iconStyle = { marginLeft: 6, verticalAlign: 'middle' } as React.CSSProperties;
    if (!isActive) return <FaSort style={iconStyle} color={color} />;
    return sortDir === 'asc' ? <FaSortUp style={iconStyle} color={color} /> : <FaSortDown style={iconStyle} color={color} />;
  };

  return (
    <div className={styles.tableCard}>
      <table data-testid={`absence-table-${tableId}`} className={styles.absTable}>
        <colgroup>
          <col style={{ width: '35px' }} />
          <col style={{ width: '115px' }} />
          <col style={{ width: '80px' }} />
          <col style={{ width: '100px' }} />
          <col style={{ width: '160px' }} />
          <col style={{ width: '45px' }} />
          <col style={{ width: '70px' }} />
          <col style={{ width: '75px' }} />
          <col style={{ width: '80px' }} />
          <col style={{ width: '160px' }} />
        </colgroup>
        <thead className={styles.thead}>
          <tr className={styles.theadRow}>
            <th className={styles.thCenter} style={{ width: '35px' }}>
              <input
                type="checkbox"
                data-testid="select-all-absences"
                checked={(() => {
                  const requestedIds = list.filter(a => a.status === 'REQUESTED').map(a => a.id);
                  return requestedIds.length > 0 && requestedIds.every(id => selectedIds.has(id));
                })()}
                onChange={() => onToggleSelectAll(list)}
                style={{ cursor: 'pointer', accentColor: '#E7007D' }}
                title="Select all pending requests"
              />
            </th>
            <th data-testid={`employee-header-${tableId}`} className={styles.thSortable} onClick={() => onSort('employee_name')}>
              Employee {renderSortIcon('employee_name')}
            </th>
            <th className={styles.th}>Type</th>
            <th className={styles.th}>Reason</th>
            <th className={styles.thSortable} onClick={() => onSort('startEnd')}>
              Start - End {renderSortIcon('startEnd')}
            </th>
            <th className={styles.thCenter}>Days</th>
            <th className={styles.th}>HTR</th>
            <th className={styles.thSortable} onClick={() => onSort('status')}>
              Status {renderSortIcon('status')}
            </th>
            <th className={styles.thSortable} onClick={() => onSort('requested_on')}>
              Requested on {renderSortIcon('requested_on')}
            </th>
            <th className={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map(abs => {
            const empName = getEmpName(abs, allEmployees);
            const startEndStr = formatStartEnd(abs);
            const dayCount = computeDayCount(abs);
            const statusLower = abs.status ? abs.status.toLowerCase() : '';
            const reqOnShort = formatReqOnShort(abs);

            return (
              <tr
                key={abs.id}
                data-testid={`absence-row-${abs.id}`}
                className={styles.absRow}
                onClick={() => onRowClick(abs, 'row')}
              >
                <td className={styles.tdCenter} style={{ width: '35px' }} onClick={e => e.stopPropagation()}>
                  {abs.status === 'REQUESTED' ? (
                    <input
                      type="checkbox"
                      data-testid={`select-absence-${abs.id}`}
                      checked={selectedIds.has(abs.id)}
                      onChange={() => onToggleSelect(abs.id)}
                      style={{ cursor: 'pointer', accentColor: '#E7007D' }}
                    />
                  ) : null}
                </td>
                <td data-testid={`employee-cell-${abs.id}`} className={styles.td}>{empName}</td>
                <td className={styles.td}>{(abs.absence_type || '').toLowerCase()}</td>
                <td className={styles.td}>{abs.reason_note || ''}</td>
                <td className={styles.td}>{startEndStr}</td>
                <td className={styles.tdCenter}>{dayCount}</td>
                <td className={styles.td}>26/14/12</td>
                <td className={styles.td} data-testid={`absence-status-${abs.id}`}>{statusLower}</td>
                <td className={styles.td}>{reqOnShort}</td>
                <td className={styles.tdActions} onClick={e => e.stopPropagation()}>
                  <div className={styles.actionBtnGroup}>
                    <button className={styles.adjustBtn} onClick={() => onRowClick(abs, 'row')} title="Adjust">
                      Adjust
                    </button>
                    {abs.status === 'REQUESTED' && (
                      <button
                        data-testid={`approve-button-${abs.id}`}
                        className={styles.approveBtn}
                        onClick={() => onApprove(abs.id)}
                        title="Approve"
                        aria-label="Approve"
                      >
                        &#10003;
                      </button>
                    )}
                    {abs.status === 'REQUESTED' && (
                      <button
                        data-testid={`reject-button-${abs.id}`}
                        className={styles.rejectBtn}
                        onClick={() => onReject(abs.id)}
                        title="Reject"
                      >
                        Reject
                      </button>
                    )}
                    <button
                      data-testid={`delete-button-${abs.id}`}
                      className={styles.deleteBtn}
                      onClick={() => onDelete(abs.id)}
                      title="Delete"
                      aria-label="Delete"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
